import { useState, useRef, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Loader2 } from "lucide-react";

interface FileUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: File, displayName: string, description: string) => void;
  isPending: boolean;
  title?: string;
  dialogDescription?: string;
  children?: ReactNode;
}

export function FileUploadDialog({
  open,
  onOpenChange,
  onUpload,
  isPending,
  title = "Upload File",
  dialogDescription = "Upload a file to the system.",
  children,
}: FileUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const userEditedName = useRef(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");

  function reset() {
    setSelectedFile(null);
    setDisplayName("");
    setDescription("");
    userEditedName.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && !userEditedName.current) {
      setDisplayName(file.name.replace(/\.[^/.]+$/, ""));
    }
  }

  function handleDisplayNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDisplayName(e.target.value);
    userEditedName.current = true;
  }

  function handleSubmit() {
    if (!selectedFile) return;
    onUpload(selectedFile, displayName.trim(), description.trim());
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle data-testid="text-upload-dialog-title">{title}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 -mx-6 px-6 pb-2">
          <div>
            <Label htmlFor="upload-file">File</Label>
            <Input
              ref={fileInputRef}
              id="upload-file"
              type="file"
              onChange={handleFileChange}
              data-testid="input-upload-file"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="upload-display-name">Display Name (optional)</Label>
            <Input
              id="upload-display-name"
              value={displayName}
              onChange={handleDisplayNameChange}
              placeholder="Friendly name for this file"
              data-testid="input-upload-display-name"
              className="mt-1"
              maxLength={150}
            />
          </div>
          <div>
            <Label htmlFor="upload-description">Description (optional)</Label>
            <Textarea
              id="upload-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this file"
              data-testid="input-upload-description"
              className="mt-1 resize-none"
              rows={2}
              maxLength={500}
            />
          </div>
          {children}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            data-testid="button-cancel-upload"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedFile || isPending}
            data-testid="button-submit-upload"
          >
            {isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />Upload</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
